import { useTranslation } from "react-i18next";
import { MessageCircle, Mail, Clock, Languages } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Link } from "@/i18n/routing";
import { CONTACT, waLink, mailLink, LOCATIONS } from "@/data/site";

const FooterCol = ({ title, children }) => (
  <div>
    <h4 className="text-sm font-bold uppercase tracking-[0.14em] text-white">{title}</h4>
    <ul className="mt-4 space-y-2.5">{children}</ul>
  </div>
);

const FLink = ({ to, children, testid }) => (
  <li>
    <Link to={to} data-testid={testid} className="text-sm text-white/70 transition-colors hover:text-[hsl(150_62%_60%)]">{children}</Link>
  </li>
);

export const Footer = () => {
  const { t } = useTranslation();
  return (
    <footer className="bg-footer text-white">
      <div className="vc-container grid gap-10 py-14 md:grid-cols-2 lg:grid-cols-12">
        <div className="lg:col-span-4">
          <Logo variant="light" />
          <p className="mt-5 max-w-xs text-sm leading-relaxed text-white/65">{t("footer.about")}</p>
          <p className="mt-4 max-w-sm text-xs leading-relaxed text-white/45" data-testid="footer-disclaimer">{t("footer.disclaimer")}</p>
          <p className="mt-5 text-xs text-white/50">© {new Date().getFullYear()} {CONTACT.brand}. {t("footer.rights")}</p>
        </div>

        <div className="lg:col-span-2">
          <FooterCol title={t("footer.quickLinks")}>
            <FLink to="/urgent-care" testid="footer-link-urgent">{t("nav.urgentCare")}</FLink>
            <FLink to="/nursing" testid="footer-link-nursing">{t("nav.nursing")}</FLink>
            <FLink to="/common-health-problems-vietnam" testid="footer-link-common">{t("nav.commonHealth")}</FLink>
            <FLink to="/locations" testid="footer-link-locations">{t("nav.locations")}</FLink>
            <FLink to="/medical-tourism" testid="footer-link-tourism">{t("nav.medicalTourism")}</FLink>
            <FLink to="/about" testid="footer-link-about">{t("nav.about")}</FLink>
            <FLink to="/contact" testid="footer-link-contact">{t("nav.contact")}</FLink>
            <FLink to="/privacy" testid="footer-link-privacy">{t("footer.privacy")}</FLink>
            <FLink to="/terms" testid="footer-link-terms">{t("footer.terms")}</FLink>
          </FooterCol>
        </div>

        <div className="lg:col-span-3">
          <FooterCol title={t("footer.ourServices")}>
            <FLink to="/urgent-care/doctor-to-hotel">{t("nav.doctorToHotel")}</FLink>
            <FLink to="/urgent-care/online-consultation">{t("nav.onlineConsultation")}</FLink>
            <FLink to="/urgent-care/clinic-booking">{t("nav.clinicBooking")}</FLink>
            <FLink to="/urgent-care/specialist-referral">{t("nav.specialistReferral")}</FLink>
            <FLink to="/urgent-care/post-discharge-support">{t("nav.postDischarge")}</FLink>
            <FLink to="/nursing/post-operative">{t("nav.postOperative")}</FLink>
          </FooterCol>
        </div>

        <div className="lg:col-span-3">
          <FooterCol title={t("footer.contactUs")}>
            <li>
              <a href={waLink()} target="_blank" rel="noopener noreferrer" className="flex items-center gap-2 text-sm text-white/75 hover:text-[hsl(150_62%_60%)]" data-testid="footer-whatsapp">
                <MessageCircle className="h-4 w-4 text-[hsl(150_62%_55%)]" /> {CONTACT.whatsappDisplay}
              </a>
            </li>
            <li>
              <a href={mailLink()} className="flex items-center gap-2 text-sm text-white/75 hover:text-[hsl(150_62%_60%)]" data-testid="footer-email">
                <Mail className="h-4 w-4 text-[hsl(150_62%_55%)]" /> {CONTACT.email}
              </a>
            </li>
            <li className="flex items-center gap-2 text-sm text-white/70">
              <Clock className="h-4 w-4 text-[hsl(150_62%_55%)]" /> {t("footer.whatsapp247")}
            </li>
            <li className="flex items-start gap-2 text-sm text-white/70">
              <Languages className="mt-0.5 h-4 w-4 shrink-0 text-[hsl(150_62%_55%)]" /> {CONTACT.languages.join(", ")}
            </li>
          </FooterCol>

          <div className="mt-6 rounded-xl border border-white/10 bg-[hsl(var(--vc-footer-2))] p-4" data-testid="footer-supported-by">
            <p className="text-xs font-bold uppercase tracking-wide text-white/80">{t("footer.supportedBy")}</p>
            <p className="mt-2 text-xs leading-relaxed text-white/60">
              Doctors Vietnam is supported by{" "}
              <a
                href="https://www.vietclinics.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-semibold text-[hsl(150_62%_65%)] underline underline-offset-2 hover:text-[hsl(150_62%_75%)]"
                data-testid="footer-vietclinics-link"
              >
                VietClinics
              </a>
              , an independent healthcare coordination platform in Vietnam.
            </p>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="vc-container flex flex-col items-center justify-between gap-3 py-5 sm:flex-row">
          <p className="text-xs text-white/50">{t("footer.serving")} {LOCATIONS.map((l) => l.name).join(" · ")}</p>
          <p className="text-xs text-white/50">{CONTACT.brandDomain}</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
